import { useEffect, useRef } from "react";
import {
  View,
  Text,
  Animated,
  TouchableOpacity,
  StyleSheet,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";

const Toast = ({ toast, onDismiss }) => {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const translateAnim = useRef(new Animated.Value(-20)).current;
  const progressAnim = useRef(new Animated.Value(100)).current;

  useEffect(() => {
    // Entry animation
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(translateAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start();

    // Progress bar animation (only for auto-dismiss toasts)
    if (toast.duration) {
      Animated.timing(progressAnim, {
        toValue: 0,
        duration: toast.duration,
        useNativeDriver: false,
      }).start();

      // Auto dismiss
      const timer = setTimeout(() => {
        dismissToast();
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, []);

  const dismissToast = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(translateAnim, {
        toValue: -20,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onDismiss(toast.id);
    });
  };

  const progressWidth = progressAnim.interpolate({
    inputRange: [0, 100],
    outputRange: ["0%", "100%"],
  });

  const renderIcon = () => {
    const color = getIconColor();
    if (toast.type === "error") {
      return <Ionicons name="alert-outline" size={14} color={color} />;
    }
    if (toast.type === "warning") {
      return <Ionicons name="information-outline" size={14} color={color} />;
    }
    return <Ionicons name="checkmark-outline" size={14} color={color} />;
  };

  const getIconBackgroundColor = () => {
    if (toast.type === "error") return "#ff4d4f33";
    if (toast.type === "warning") return "rgba(255, 167, 38, 0.25)";
    return "#3f7b8d55";
  };

  const getIconColor = () => {
    if (toast.type === "error") return "#ff9a9c";
    if (toast.type === "warning") return "#FFA726";
    return "#b9dfe7";
  };

  const getToastStyle = () => {
    if (toast.type === "error") return styles.toastError;
    if (toast.type === "warning") {
      // Only show left border if no timeout (manual close only)
      return toast.duration ? styles.toastWarning : styles.toastWarningPersistent;
    }
    return styles.toastInfo;
  };

  const getProgressBarColor = () => {
    if (toast.type === "warning") return "#FFA726";
    return "#3f7b8d";
  };

  return (
    <Animated.View
      style={[
        styles.toast,
        getToastStyle(),
        {
          opacity: fadeAnim,
          transform: [{ translateY: translateAnim }],
        },
      ]}
    >
      {toast.duration && (
        <Animated.View
          style={[
            styles.progressBar,
            {
              width: progressWidth,
              backgroundColor: getProgressBarColor(),
            },
          ]}
        />
      )}
      <View
        style={[styles.pillIcon, { backgroundColor: getIconBackgroundColor() }]}
      >
        {renderIcon()}
      </View>
      <View style={styles.mainContent}>
        <Text style={styles.toastTitle}>{toast.title}</Text>
        {toast.description && (
          <Text
            style={[
              styles.toastDescription,
              toast.type === "error" && styles.toastDescriptionError,
              toast.type === "warning" && styles.toastDescriptionWarning,
            ]}
          >
            {toast.description}
          </Text>
        )}
      </View>
      <TouchableOpacity
        style={styles.closeButton}
        onPress={dismissToast}
        activeOpacity={0.7}
      >
        <Text style={styles.closeText}>×</Text>
      </TouchableOpacity>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  toast: {
    position: "relative",
    minWidth: 240,
    maxWidth: 360,
    paddingTop: 12,
    paddingBottom: 12,
    paddingLeft: 12,
    paddingRight: 40,
    borderRadius: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.7,
    shadowRadius: 30,
    elevation: 10,
    marginBottom: 8,
    overflow: "hidden",
    flexDirection: "row",
    alignItems: "flex-start",
  },
  toastInfo: {
    backgroundColor: "#151a20",
    borderWidth: 1,
    borderColor: "#3f7b8d33",
  },
  toastError: {
    backgroundColor: "#201214",
    borderWidth: 1,
    borderLeftWidth: 3,
    borderColor: "#ff4d4f33",
    borderLeftColor: "#ff4d4f",
  },
  toastWarning: {
    backgroundColor: "#1f1a14",
    borderWidth: 1,
    borderColor: "rgba(255, 167, 38, 0.2)",
  },
  toastWarningPersistent: {
    backgroundColor: "#1f1a14",
    borderWidth: 1,
    borderLeftWidth: 3,
    borderColor: "rgba(255, 167, 38, 0.2)",
    borderLeftColor: "#FFA726",
  },
  progressBar: {
    position: "absolute",
    top: 0,
    left: 0,
    height: 3,
    backgroundColor: "#3f7b8d",
  },
  pillIcon: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 1,
    marginRight: 10,
    flexShrink: 0,
  },
  pillIconText: {
    fontSize: 13,
    fontWeight: "700",
  },
  mainContent: {
    flex: 1,
    gap: 2,
  },
  toastTitle: {
    color: "#ffffff",
    fontSize: 13,
    fontWeight: "600",
    marginBottom: 4,
    // fontFamily:
    //   '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  },
  toastDescription: {
    color: "#d7e1e4",
    fontSize: 12,
    // fontFamily:
    //   '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  },
  toastDescriptionError: {
    color: "#e6d4d4",
  },
  toastDescriptionWarning: {
    color: "#e6ddd4",
  },
  closeButton: {
    position: "absolute",
    top: 6,
    right: 8,
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: "transparent",
    alignItems: "center",
    justifyContent: "center",
  },
  closeText: {
    color: "#b3b3b3",
    fontSize: 20,
    lineHeight: 22,
    fontWeight: "300",
  },
});

export default Toast;
